import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  PlayArrow as PlayIcon,
  CalendarToday as CalendarIcon,
  Article as ArticleIcon,
  Campaign as CampaignIcon,
  Payment as PaymentIcon,
  Assessment as AssessmentIcon,
  Verified as VerifiedIcon,
  Public as PublicIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface TimelineEvent {
  id: string;
  type: 'pr_submitted' | 'pr_approved' | 'pr_published' | 'pr_rejected' | 'pr_distributed' | 
        'payment_completed' | 'payment_failed' | 'payment_refunded' | 
        'campaign_created' | 'campaign_started' | 'campaign_completed' |
        'verification_completed' | 'verification_rejected' |
        'distribution_sent' | 'distribution_opened' | 'distribution_published' |
        'article_tracked' | 'metrics_updated';
  title: string;
  description: string;
  timestamp: string;
  status: 'completed' | 'pending' | 'failed' | 'in_progress';
  user: string;
  details: {
    prId?: string;
    campaignId?: string;
    paymentId?: string;
    distributionId?: string;
    channelId?: string;
    amount?: string;
    channels?: number;
    views?: number;
    engagement?: number;
    url?: string;
  };
  metadata?: {
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    tags?: string[];
    notes?: string;
  };
}

interface TimelineStats {
  total: number;
  completed: number;
  pending: number;
  in_progress: number;
  failed: number;
  published: number;
}

const TimelinePage: React.FC = () => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<TimelineEvent[]>([]);
  const [stats, setStats] = useState<TimelineStats>({
    total: 0,
    completed: 0,
    pending: 0,
    in_progress: 0,
    failed: 0,
    published: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      // Build query parameters
      const params: any = {
        page: page + 1,
        limit: rowsPerPage * 10, // Load more for filtering
      };

      if (typeFilter !== 'all') {
        params.type = typeFilter;
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      // Handle date filters
      if (dateFilter === 'custom' && startDate && endDate) {
        params.startDate = startDate.toISOString();
        params.endDate = endDate.toISOString();
      } else if (dateFilter !== 'all' && dateFilter !== 'custom') {
        const now = new Date();
        let start: Date;
        switch (dateFilter) {
          case 'today':
            start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            params.startDate = start.toISOString();
            params.endDate = now.toISOString();
            break;
          case 'week':
            start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            params.startDate = start.toISOString();
            params.endDate = now.toISOString();
            break;
          case 'month':
            start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            params.startDate = start.toISOString();
            params.endDate = now.toISOString();
            break;
          case 'quarter':
            start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            params.startDate = start.toISOString();
            params.endDate = now.toISOString();
            break;
        }
      }

      const response = await axios.get(`${API_BASE_URL}/api/timeline/events`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        params,
      });

      if (response.data.success) {
        const eventsData = response.data.events.map((event: any) => ({
          ...event,
          timestamp: new Date(event.timestamp).toLocaleString(),
        }));
        setEvents(eventsData);
        setFilteredEvents(eventsData);
        setStats(response.data.stats);
      } else {
        setError(response.data.message || 'Failed to load events');
      }
    } catch (err: any) {
      console.error('Error loading timeline events:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load timeline events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [page, rowsPerPage, typeFilter, statusFilter, dateFilter, startDate, endDate]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm || (!searchTerm && events.length > 0)) {
        loadEvents();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'pr_submitted': return <ArticleIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'pr_approved': return <CheckCircleIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'pr_published': return <PublicIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'pr_rejected': return <ErrorIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'pr_distributed': return <SendIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'payment_completed': return <PaymentIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'payment_failed': return <ErrorIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'payment_refunded': return <PaymentIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'campaign_created': return <CampaignIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'campaign_started': return <PlayIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'campaign_completed': return <CheckCircleIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'verification_completed': return <VerifiedIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'verification_rejected': return <ErrorIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'distribution_sent': return <SendIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'distribution_published': return <PublicIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'article_tracked': return <AssessmentIcon style={{ fontSize: '16px', color: 'white' }} />;
      case 'metrics_updated': return <AssessmentIcon style={{ fontSize: '16px', color: 'white' }} />;
      default: return <TimelineIcon style={{ fontSize: '16px', color: 'white' }} />;
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'pr_submitted': return '#2196F3';
      case 'pr_approved': return '#4CAF50';
      case 'pr_published': return '#FF9800';
      case 'pr_rejected': return '#F44336';
      case 'pr_distributed': return '#9C27B0';
      case 'payment_completed': return '#4CAF50';
      case 'payment_failed': return '#F44336';
      case 'payment_refunded': return '#FF9800';
      case 'campaign_created': return '#2196F3';
      case 'campaign_started': return '#00BCD4';
      case 'campaign_completed': return '#4CAF50';
      case 'verification_completed': return '#4CAF50';
      case 'verification_rejected': return '#F44336';
      case 'distribution_sent': return '#2196F3';
      case 'distribution_published': return '#FF9800';
      case 'article_tracked': return '#795548';
      case 'metrics_updated': return '#607D8B';
      default: return '#9E9E9E';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon style={{ color: '#4CAF50' }} />;
      case 'pending': return <PendingIcon style={{ color: '#FF9800' }} />;
      case 'failed': return <ErrorIcon style={{ color: '#F44336' }} />;
      case 'in_progress': return <PlayIcon style={{ color: '#2196F3' }} />;
      default: return <PendingIcon style={{ color: '#9E9E9E' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'pending': return '#FF9800';
      case 'failed': return '#F44336';
      case 'in_progress': return '#2196F3';
      default: return '#9E9E9E';
    }
  };

  const getEventSteps = (event: TimelineEvent) => {
    const steps = [];
    
    switch (event.type) {
      case 'pr_submitted':
        steps.push('Draft Created', 'Submitted for Review', 'Under Review');
        if (event.status === 'completed') steps.push('Approved');
        if (event.status === 'failed') steps.push('Rejected');
        break;
      case 'pr_distributed':
        steps.push('Distribution Requested', 'Channels Selected', 'Distribution Sent', 'Awaiting Publication');
        if (event.status === 'completed') steps.push('Published');
        break;
      case 'payment_completed':
        steps.push('Payment Initiated', 'Processing', 'Completed');
        break;
      case 'campaign_created':
        steps.push('Campaign Created', 'Press Releases Added', 'Distribution Scheduled');
        if (event.status === 'completed') steps.push('Campaign Completed');
        break;
      default:
        steps.push('Event Started');
        if (event.status === 'completed') steps.push('Completed');
        if (event.status === 'failed') steps.push('Failed');
    }
    
    return steps;
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, timelineEvent: TimelineEvent) => {
    setAnchorEl(event.currentTarget);
    setSelectedEvent(timelineEvent);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const handleViewEvent = () => {
    setViewDialogOpen(true);
    handleMenuClose();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportTimeline = async () => {
    try {
      const token = localStorage.getItem('token');
      const csvContent = [
        ['Event', 'Type', 'Status', 'User', 'Timestamp', 'Description'].join(','),
        ...filteredEvents.map(e => [
          `"${e.title}"`,
          `"${e.type}"`,
          `"${e.status}"`,
          `"${e.user}"`,
          `"${e.timestamp}"`,
          `"${e.description}"`
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `timeline-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export timeline error:', err);
    }
  };

  return (
    <Container maxWidth="xl" style={{ padding: '24px' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Typography variant="h4" style={{ fontWeight: 'bold', color: '#0927eb', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <TimelineIcon style={{ fontSize: '32px' }} />
              Activity Timeline
            </Typography>
            <Typography variant="body1" style={{ color: '#666' }}>
              Track all activities and events across your PR campaigns
            </Typography>
          </div>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadEvents}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <Grid container spacing={3} style={{ marginBottom: '32px' }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card style={{ borderRadius: '12px' }}>
              <CardContent>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Typography variant="h4" style={{ fontWeight: 'bold', color: '#0927eb' }}>
                      {stats.total}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#666' }}>
                      Total Events
                    </Typography>
                  </div>
                  <TimelineIcon style={{ fontSize: '40px', color: '#0927eb', opacity: 0.3 }} />
                </div>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card style={{ borderRadius: '12px' }}>
              <CardContent>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4CAF50' }}>
                      {stats.completed}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#666' }}>
                      Completed
                    </Typography>
                  </div>
                  <CheckCircleIcon style={{ fontSize: '40px', color: '#4CAF50', opacity: 0.3 }} />
                </div>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card style={{ borderRadius: '12px' }}>
              <CardContent>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Typography variant="h4" style={{ fontWeight: 'bold', color: '#FF9800' }}>
                      {stats.pending}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#666' }}>
                      Pending
                    </Typography>
                  </div>
                  <PendingIcon style={{ fontSize: '40px', color: '#FF9800', opacity: 0.3 }} />
                </div>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card style={{ borderRadius: '12px' }}>
              <CardContent>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2196F3' }}>
                      {stats.published}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#666' }}>
                      Published
                    </Typography>
                  </div>
                  <PublicIcon style={{ fontSize: '40px', color: '#2196F3', opacity: 0.3 }} />
                </div>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          </Box>
        )}

        {/* Filters and Search */}
        <Paper style={{ padding: '24px', marginBottom: '24px', borderRadius: '12px' }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search timeline..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="pr_submitted">PR Submitted</MenuItem>
                  <MenuItem value="pr_approved">PR Approved</MenuItem>
                  <MenuItem value="pr_published">PR Published</MenuItem>
                  <MenuItem value="pr_rejected">PR Rejected</MenuItem>
                  <MenuItem value="pr_distributed">PR Distributed</MenuItem>
                  <MenuItem value="payment_completed">Payment Completed</MenuItem>
                  <MenuItem value="payment_failed">Payment Failed</MenuItem>
                  <MenuItem value="payment_refunded">Payment Refunded</MenuItem>
                  <MenuItem value="campaign_created">Campaign Created</MenuItem>
                  <MenuItem value="campaign_started">Campaign Started</MenuItem>
                  <MenuItem value="campaign_completed">Campaign Completed</MenuItem>
                  <MenuItem value="verification_completed">Verification Completed</MenuItem>
                  <MenuItem value="distribution_published">Distribution Published</MenuItem>
                  <MenuItem value="article_tracked">Article Tracked</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateFilter}
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    if (e.target.value !== 'custom') {
                      setStartDate(null);
                      setEndDate(null);
                    }
                  }}
                  label="Date Range"
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="quarter">This Quarter</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                fullWidth
                style={{
                  borderColor: '#000000',
                  color: '#000000'
                }}
                onClick={handleExportTimeline}
              >
                Export Timeline
              </Button>
            </Grid>
            {dateFilter === 'custom' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate ? startDate.toISOString().split('T')[0] : ''}
                    onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate ? endDate.toISOString().split('T')[0] : ''}
                    onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {/* Timeline View */}
        <Paper style={{ padding: '24px', marginBottom: '24px', borderRadius: '12px' }}>
          <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '24px' }}>
            Recent Activity Timeline
          </Typography>
          <div style={{ position: 'relative' }}>
            {filteredEvents.slice(0, 10).map((event, index) => (
              <div key={event.id} style={{ display: 'flex', marginBottom: '24px', position: 'relative' }}>
                {/* Timeline line */}
                {index < filteredEvents.slice(0, 10).length - 1 && (
                  <div style={{
                    position: 'absolute',
                    left: '20px',
                    top: '40px',
                    width: '2px',
                    height: 'calc(100% + 24px)',
                    backgroundColor: '#e0e0e0',
                    zIndex: 1
                  }} />
                )}
                
                {/* Timeline dot */}
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: getEventColor(event.type),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '16px',
                  zIndex: 2,
                  position: 'relative'
                }}>
                  {getEventIcon(event.type)}
                </div>
                
                {/* Timeline content */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                      {event.title}
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography variant="body2" style={{ color: '#666' }}>
                        {event.timestamp}
                      </Typography>
                      <Chip
                        icon={getStatusIcon(event.status)}
                        label={event.status.charAt(0).toUpperCase() + event.status.slice(1).replace('_', ' ')}
                        style={{
                          backgroundColor: getStatusColor(event.status),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </div>
                  </div>
                  <Typography variant="body2" style={{ color: '#666', marginBottom: '8px' }}>
                    {event.description}
                  </Typography>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Avatar style={{ width: '20px', height: '20px', fontSize: '10px' }}>
                      {event.user.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                    <Typography variant="body2" style={{ color: '#666' }}>
                      {event.user}
                    </Typography>
                  </div>
                  {event.metadata?.tags && (
                    <div>
                      {event.metadata.tags.map((tag, tagIndex) => (
                        <Chip
                          key={tagIndex}
                          label={tag}
                          size="small"
                          style={{ marginRight: '4px', marginBottom: '2px' }}
                        />
                      ))}
                    </div>
                  )}
                  {/* Event Steps */}
                  <Box sx={{ mt: 2 }}>
                    <Stepper orientation="vertical" activeStep={getEventSteps(event).length - 1}>
                      {getEventSteps(event).map((step, stepIndex) => (
                        <Step key={stepIndex}>
                          <StepLabel>{step}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>
                </div>
              </div>
            ))}
          </div>
        </Paper>

        {/* Events Table */}
        <TableContainer component={Paper} style={{ borderRadius: '12px' }}>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                <TableCell style={{ fontWeight: 'bold' }}>Event</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>User</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Timestamp</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEvents
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((event) => (
                  <TableRow key={event.id} hover>
                    <TableCell>
                      <div>
                        <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                          {event.title}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#666' }}>
                          {event.description}
                        </Typography>
                        {event.metadata?.tags && (
                          <div style={{ marginTop: '4px' }}>
                            {event.metadata.tags.map((tag, index) => (
                              <Chip
                                key={index}
                                label={tag}
                                size="small"
                                style={{ marginRight: '4px', marginBottom: '2px' }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ 
                          width: '24px', 
                          height: '24px', 
                          borderRadius: '50%', 
                          backgroundColor: getEventColor(event.type),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {getEventIcon(event.type)}
                        </div>
                        <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                          {event.type.replace(/_/g, ' ')}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(event.status)}
                        label={event.status.charAt(0).toUpperCase() + event.status.slice(1).replace('_', ' ')}
                        style={{
                          backgroundColor: getStatusColor(event.status),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar style={{ width: '24px', height: '24px', fontSize: '12px' }}>
                          {event.user.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        {event.user}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CalendarIcon style={{ fontSize: '16px', color: '#666' }} />
                        {event.timestamp}
                      </div>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => handleMenuClick(e, event)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredEvents.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleViewEvent}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportTimeline}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export</ListItemText>
          </MenuItem>
        </Menu>

        {/* View Event Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h5" style={{ fontWeight: 'bold' }}>
              {selectedEvent?.title}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {selectedEvent && (
              <div style={{ marginTop: '16px' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      Event Information
                    </Typography>
                    <div style={{ marginBottom: '16px' }}>
                      <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                        Type
                      </Typography>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ 
                          width: '24px', 
                          height: '24px', 
                          borderRadius: '50%', 
                          backgroundColor: getEventColor(selectedEvent.type),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {getEventIcon(selectedEvent.type)}
                        </div>
                        <Typography variant="subtitle2" style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                          {selectedEvent.type.replace(/_/g, ' ')}
                        </Typography>
                      </div>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                        Status
                      </Typography>
                      <Chip
                        icon={getStatusIcon(selectedEvent.status)}
                        label={selectedEvent.status.charAt(0).toUpperCase() + selectedEvent.status.slice(1).replace('_', ' ')}
                        style={{
                          backgroundColor: getStatusColor(selectedEvent.status),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                        User
                      </Typography>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar style={{ width: '24px', height: '24px', fontSize: '12px' }}>
                          {selectedEvent.user.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                          {selectedEvent.user}
                        </Typography>
                      </div>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                        Timestamp
                      </Typography>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        {selectedEvent.timestamp}
                      </Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      Event Details
                    </Typography>
                    {Object.entries(selectedEvent.details).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: '16px' }}>
                        <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                          {key.charAt(0).toUpperCase() + key.slice(1)}
                        </Typography>
                        <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                          {value}
                        </Typography>
                      </div>
                    ))}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      Description
                    </Typography>
                    <Typography variant="body2" style={{ color: '#666' }}>
                      {selectedEvent.description}
                    </Typography>
                  </Grid>
                  {selectedEvent.metadata?.tags && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                        Tags
                      </Typography>
                      <div>
                        {selectedEvent.metadata.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            style={{ marginRight: '8px', marginBottom: '4px' }}
                          />
                        ))}
                      </div>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      Event Timeline
                    </Typography>
                    <Stepper orientation="vertical" activeStep={getEventSteps(selectedEvent).length - 1}>
                      {getEventSteps(selectedEvent).map((step, stepIndex) => (
                        <Step key={stepIndex}>
                          <StepLabel>{step}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Grid>
                </Grid>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
            <Button
              variant="contained"
              style={{
                backgroundColor: '#000000',
                color: 'white'
              }}
              onClick={handleExportTimeline}
            >
              Export Event
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
};

export default TimelinePage;
